const firstConfig=globalThis.document?.querySelector('santic-fit-guide [data-fit-config]');
const assetConfig=firstConfig?JSON.parse(firstConfig.textContent):{};
const [{catalog},{recommend},{productImages}]=await Promise.all([
  import(assetConfig.catalog || new URL('./santic-fit-catalog.js',import.meta.url).href),
  import(assetConfig.engine || new URL('./santic-fit-engine.js',import.meta.url).href),
  import(assetConfig.productImages || new URL('./santic-fit-product-images.js',import.meta.url).href)
]);

const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const arrow = (back = false) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="${back ? 'M15 6l-6 6 6 6' : 'M5 12h14M13 6l6 6-6 6'}" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const tick = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m4 10 4 4 8-8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const factor = { mm:1, cm:10, in:25.4 };
const productCache = new Map();
const insoleProduct={handle:'santic-non-slip-shock-absorbing-arch-support-insole',images:{low:'https://cdn.shopify.com/s/files/1/0565/9826/6045/files/1_edb60fa1-3933-4327-a960-3e30884be436.jpg?v=1770693258&width=640',high:'https://cdn.shopify.com/s/files/1/0565/9826/6045/files/1_50f72c1b-cd59-482d-87dc-daadab52d2dc.jpg?v=1770693258&width=640'}};
let instance = 0;

export function variantMatchesSize(value, eu) {
  const text=String(value ?? '').trim();
  const explicit=text.match(/\bEU\s*[:\-]?\s*(\d{2}(?:[.,]\d+)?)/i);
  if(explicit) return Number(explicit[1].replace(',','.')) === Number(eu);
  if(/\b(?:US|UK)\b/i.test(text)) return false;
  const plain=text.match(/^(\d{2}(?:[.,]\d+)?)(?:\s*(?:$|\(|\[|\/|EU\b))/i);
  return Boolean(plain && Number(plain[1].replace(',','.')) === Number(eu));
}

class SanticFitGuide extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if(!this.started) {
      this.started=true;
      this.uid=`santic-fit-${++instance}`;
      this.copy=JSON.parse(this.querySelector('[data-fit-i18n]').textContent);
      this.config=JSON.parse(this.querySelector('[data-fit-config]').textContent);
      this.answers={pedal:null,lengthMm:null,widthMm:null,instep:null,arch:null,toeShape:null,toeComfort:null};
      this.unit='mm'; this.widthUnit='mm'; this.index=0; this.showingResults=false; this.renderToken=0;
      this.stage=this.querySelector('[data-stage]');
    }
    this.events?.abort();
    this.events=new AbortController();
    this.addEventListener('click',e=>this.onClick(e),{signal:this.events.signal});
    this.addEventListener('change',e=>this.onChange(e),{signal:this.events.signal});
    this.addEventListener('input',e=>this.onInput(e),{signal:this.events.signal});
    this.addEventListener('submit',e=>this.onSubmit(e),{signal:this.events.signal});
    this.render(false);
  }

  disconnectedCallback() { this.events?.abort(); this.renderToken++; }
  t(key) { return this.copy[key] || key; }
  html(key) { return escapeHTML(this.t(key)); }
  path() {
    const steps=['length','width','instep','arch','toe'];
    if(this.answers.toeShape && this.answers.toeShape!=='none') steps.push('comfort');
    steps.push('pedal');
    return steps;
  }

  progress() {
    const steps=this.path();
    this.querySelector('[data-progress]').hidden=this.showingResults;
    this.querySelector('[data-action="restart"]').hidden=!this.showingResults && this.index===0;
    this.querySelector('[data-step-count]').textContent=`${this.t('step')} ${this.index+1} ${this.t('of')} ${steps.length}`;
    this.querySelector('[data-progress-list]').innerHTML=steps.map((s,i)=>`<li ${i===this.index?'aria-current="step"':''} class="${i<this.index?'is-done':''}"><span class="fit-sr-only">${escapeHTML(this.t(`${s==='toe'?'toe':s}_title`))}</span></li>`).join('');
  }

  onClick(event) {
    const button=event.target.closest('[data-action]');
    if(!button || !this.contains(button)) return;
    const action=button.dataset.action;
    if(action==='restart') {
      this.answers={pedal:null,lengthMm:null,widthMm:null,instep:null,arch:null,toeShape:null,toeComfort:null};
      this.index=0; this.unit='mm'; this.widthUnit='mm'; this.showingResults=false; this.render();
    } else if(action==='back') {
      this.index=Math.max(0,this.index-1); this.showingResults=false; this.render();
    } else if(action==='edit') {
      this.showingResults=false; this.index=0; this.render();
    } else if(action==='more') {
      this.visibleCount+=3; this.renderCards();
    }
  }

  onChange(event) {
    const target=event.target;
    if(target.matches('[data-unit]')) {
      this.unit=target.value;
      const field=this.querySelector('[data-length]');
      field.value=this.answers.lengthMm===null?'':this.displayLength(this.answers.lengthMm);
      this.updateRange();
    }
    if(target.matches('[data-width-unit]')) {
      this.widthUnit=target.value;
      this.querySelector('[data-width]').value=this.answers.widthMm===null?'':this.displayWidth(this.answers.widthMm);
    }
    if(target.matches('input[type="radio"][data-answer]')) {
      const key=target.dataset.answer;
      const current=this.path()[this.index];
      this.answers[key]=target.value;
      if(key==='pedal') {
        const note=this.querySelector('[data-pedal-selection]');
        if(note) note.innerHTML=this.pedalNote();
      }
      if(key==='toeShape') {
        this.answers.toeComfort=target.value==='none'?'none':null;
        this.index=this.path().indexOf(current);
        this.progress();
      }
      this.clearError();
    }
  }

  onInput(event) {
    const target=event.target;
    if(target.matches('[data-length]')) {
      const text=target.value.trim().replace(',','.');
      const n=text===''?NaN:Number(text);
      this.answers.lengthMm=Number.isFinite(n)?Math.round(n*factor[this.unit]*100)/100:null;
      this.updateRange(); this.clearError();
    }
    if(target.matches('[data-length-range]')) {
      this.answers.lengthMm=Number(target.value);
      this.querySelector('[data-length]').value=this.displayLength(this.answers.lengthMm);
      this.clearError();
    }
    if(target.matches('[data-width]')) {
      const text=target.value.trim().replace(',','.');
      const n=text===''?NaN:Number(text);
      this.answers.widthMm=Number.isFinite(n)?Math.round(n*factor[this.widthUnit]*100)/100:null;
      this.clearError();
    }
  }

  displayLength(mm) { return String(Number((mm/factor[this.unit]).toFixed(this.unit==='in'?2:this.unit==='cm'?2:1))); }
  displayWidth(mm) { return String(Number((mm/factor[this.widthUnit]).toFixed(this.widthUnit==='mm'?1:2))); }
  updateRange() {
    const range=this.querySelector('[data-length-range]');
    if(range) range.value=this.answers.lengthMm ?? 260;
  }
  clearError() {
    const error=this.querySelector('[data-error]');
    if(error) { error.hidden=true; error.textContent=''; }
    this.querySelector('[aria-invalid="true"]')?.removeAttribute('aria-invalid');
  }
  showError(key) {
    const error=this.querySelector('[data-error]');
    error.textContent=this.t(key); error.hidden=false;
    const field=this.querySelector('[data-length], [data-width]') || this.querySelector('input[type="radio"]');
    field?.setAttribute('aria-invalid','true'); field?.focus();
  }

  onSubmit(event) {
    if(!event.target.matches('[data-fit-form]')) return;
    event.preventDefault();
    const key=this.path()[this.index];
    if(key==='length') {
      const mm=this.answers.lengthMm;
      if(mm===null || !Number.isFinite(mm) || mm<190 || mm>330) return this.showError('length_error');
    } else if(key==='width') {
      const mm=this.answers.widthMm;
      if(mm===null || !Number.isFinite(mm) || mm<40 || mm>180 || mm>=this.answers.lengthMm) return this.showError('width_error');
    } else {
      const answerKey={toe:'toeShape',comfort:'toeComfort'}[key] || key;
      if(!this.answers[answerKey]) return this.showError('select_error');
    }
    if(this.index===this.path().length-1) {
      this.result=recommend(catalog,this.answers);
      this.visibleCount=3; this.showingResults=true; this.render();
      this.dispatchEvent(new CustomEvent('santic-fit:complete',{bubbles:true,detail:{count:this.result.items.length,pedal:this.answers.pedal}}));
    } else {
      this.index++; this.render();
    }
  }

  guide(quadrant,prefix,labels=[]) {
    if(prefix==='arch') {
      const source=this.config.archGuide || new URL('santic-fit-arch-guide.png',new URL(this.config.measurementGuide,location.href)).href;
      return `<figure class="fit-guide"><img class="fit-measurement-image" src="${escapeHTML(source)}" alt="${this.html('arch_guide')}" width="1122" height="1223" loading="lazy" decoding="async"><figcaption><p class="fit-guide-title">${this.html('arch_guide')}</p><p class="fit-guide-caption">${this.html('arch_caption')}</p></figcaption></figure>`;
    }
    if(prefix==='length' || prefix==='width') {
      const source=this.config.lengthGuide || new URL('santic-fit-length-guide.png',new URL(this.config.measurementGuide,location.href)).href;
      return `<figure class="fit-guide"><img class="fit-measurement-image" src="${escapeHTML(source)}" alt="${this.html(`${prefix}_guide`)}" width="1119" height="1223" ${this.index===0?'fetchpriority="high"':'loading="lazy"'} decoding="async"><figcaption><p class="fit-guide-title">${this.html(`${prefix}_guide`)}</p><p class="fit-guide-caption">${this.html(`${prefix}_caption`)}</p></figcaption></figure>`;
    }
    return `<figure class="fit-guide"><div class="fit-guide-viewport" data-quadrant="${quadrant}"><img src="${escapeHTML(this.config.guide)}" alt="${this.html(`${prefix}_guide`)}" width="1254" height="1254" ${this.index===0?'fetchpriority="high"':'loading="lazy"'} decoding="async"></div>${labels.length?`<div class="fit-guide-labels">${labels.map(k=>`<span>${this.html(k)}</span>`).join('')}</div>`:''}<figcaption><p class="fit-guide-title">${this.html(`${prefix}_guide`)}</p><p class="fit-guide-caption">${this.html(`${prefix}_caption`)}</p></figcaption></figure>`;
  }

  option(key,value,label,description=null,unknown=false) {
    const id=`${this.uid}-${key}-${value}`;
    const checked=this.answers[key]===value;
    return `<label class="fit-option ${unknown?'fit-option--unknown':''}" for="${id}"><input id="${id}" name="${this.uid}-${key}" type="radio" value="${value}" data-answer="${key}" aria-describedby="${this.uid}-error" ${checked?'checked':''}><span class="fit-option-label"><span><span class="fit-option-name">${this.html(label)}</span>${description?`<span class="fit-option-description">${this.html(description)}</span>`:''}</span><span class="fit-choice-dot" aria-hidden="true"></span></span></label>`;
  }

  choiceGroup(key,choices,short=false,allowUnknown=true) {
    return `<fieldset class="fit-options ${short?'fit-options--short':''}"><legend class="fit-sr-only">${escapeHTML(this.titleFor(this.path()[this.index]))}</legend>${choices.map(c=>this.option(key,...c)).join('')}</fieldset>${allowUnknown?this.option(key,'unknown','unknown',null,true):''}`;
  }
  titleFor(step) { return this.t(`${step}_title`); }

  pedalNote() {
    const selected=this.answers.pedal?`${this.html(`pedal_${this.answers.pedal}_note`)}<br>`:'';
    const help=`<a class="fit-inline-help" href="${escapeHTML(this.contactHref())}">${this.html('pedal_help')}</a>`;
    return selected+this.html('pedal_selection_note').replace('[help]',help);
  }

  pedalGuide() {
    const source=this.config.pedalImage || new URL('santic-fit-pedal-systems.jpg',new URL(this.config.measurementGuide,location.href)).href;
    return `<figure class="fit-guide fit-pedal-guide"><img class="fit-measurement-image" src="${escapeHTML(source)}" width="800" height="800" alt="${this.html('pedal_guide')}" loading="lazy" decoding="async"><figcaption><p class="fit-guide-title">${this.html('pedal_guide')}</p><p class="fit-guide-caption">${this.html('pedal_caption')}</p></figcaption></figure>`;
  }

  render(focus=true) {
    const token=++this.renderToken;
    this.progress();
    if(this.showingResults) {
      this.renderResult();
    } else {
      const step=this.path()[this.index];
      let controls='',guide='';
      if(step==='pedal') {
        controls=this.choiceGroup('pedal',['look','peloton','spd','spd_sl','flat'].map(v=>[v,`pedal_${v}`,`pedal_${v}_desc`]),false,false)+`<p class="fit-field-note" data-pedal-selection aria-live="polite">${this.pedalNote()}</p>`;
        guide=this.pedalGuide();
      } else if(step==='length') {
        const value=this.answers.lengthMm===null?'':this.displayLength(this.answers.lengthMm);
        controls=`<label class="fit-measure-label" for="${this.uid}-length">${this.html('length_label')}</label><div class="fit-measure-row"><input id="${this.uid}-length" data-length type="text" inputmode="decimal" autocomplete="off" maxlength="8" value="${escapeHTML(value)}" placeholder="${this.html('length_placeholder')}" aria-describedby="${this.uid}-length-note ${this.uid}-error"><select class="fit-unit" data-unit aria-label="${this.html('length_label')}">${['mm','cm','in'].map(u=>`<option value="${u}" ${u===this.unit?'selected':''}>${this.html(`unit_${u}`)}</option>`).join('')}</select></div><input type="range" class="fit-range" data-length-range min="190" max="330" step="1" value="${this.answers.lengthMm ?? 260}" aria-label="${this.html('length_label')} (mm)"><div class="fit-range-labels" aria-hidden="true"><span>190 mm</span><span>330 mm</span></div><p id="${this.uid}-length-note" class="fit-field-note">${this.html('length_note')}</p>`;
        guide=this.guide(0,'length');
      } else if(step==='width') {
        const value=this.answers.widthMm===null?'':this.displayWidth(this.answers.widthMm);
        controls=`<label class="fit-measure-label" for="${this.uid}-width">${this.html('width_label')}</label><div class="fit-measure-row"><input id="${this.uid}-width" data-width type="text" inputmode="decimal" autocomplete="off" maxlength="8" value="${escapeHTML(value)}" placeholder="${this.html('width_placeholder')}" aria-describedby="${this.uid}-width-note ${this.uid}-error"><select class="fit-unit" data-width-unit aria-label="${this.html('width_label')}">${['mm','cm','in'].map(u=>`<option value="${u}" ${u===this.widthUnit?'selected':''}>${this.html(`unit_${u}`)}</option>`).join('')}</select></div><p id="${this.uid}-width-note" class="fit-field-note">${this.html('width_note')}</p>`;
        guide=this.guide(0,'width');
      } else if(step==='instep') {
        controls=this.choiceGroup('instep',[['low','low'],['normal','normal'],['high','high']],true);
        guide=this.guide(1,'instep',['low','normal','high']);
      } else if(step==='arch') {
        controls=this.choiceGroup('arch',[['low','low_arch'],['normal','normal_arch'],['high','high_arch']],true);
        guide=this.guide(2,'arch',['low_arch','normal_arch','high_arch']);
      } else if(step==='toe') {
        controls=this.choiceGroup('toeShape',[['none','toe_none'],['slight','toe_slight'],['noticeable','toe_noticeable']]);
        guide=this.guide(3,'toe',['toe_none','toe_slight','toe_noticeable']);
      } else if(step==='comfort') {
        controls=this.choiceGroup('toeComfort',[['none','comfort_none','comfort_none_desc'],['occasional','comfort_occasional','comfort_occasional_desc'],['frequent','comfort_frequent','comfort_frequent_desc']]);
        guide=this.guide(3,'toe',['toe_none','toe_slight','toe_noticeable']);
      }
      this.stage.innerHTML=`<div class="fit-layout fit-layout--${step} fit-stage-enter"><header class="fit-heading"><h1 class="fit-title" tabindex="-1" data-focus>${escapeHTML(this.titleFor(step))}</h1><p class="fit-intro">${this.html(`${step}_intro`)}</p></header><form class="fit-controls" data-fit-form novalidate>${controls}<p id="${this.uid}-error" class="fit-error" data-error role="alert" hidden></p><div class="fit-actions">${this.index?`<button type="button" class="fit-back" data-action="back">${arrow(true)}${this.html('back')}</button>`:''}<button type="submit" class="fit-button">${this.html(this.index===this.path().length-1?'finish':'next')}${arrow()}</button></div></form>${guide}</div>`;
    }
    if(focus) {
      this.stage.querySelector('[data-focus]')?.focus({preventScroll:true});
      if(this.getBoundingClientRect().top<0) this.scrollIntoView({behavior:'instant',block:'start'});
    }
    this.dispatchEvent(new CustomEvent('santic-fit:step',{bubbles:true,detail:{step:this.showingResults?'results':this.path()[this.index]}}));
    return token;
  }

  summary() {
    return `<div class="fit-summary"><span>${this.html('measurement_summary')}: <strong>${escapeHTML(this.answers.lengthMm)} mm</strong></span><span>${this.html('width_label')}: <strong>${escapeHTML(this.answers.widthMm)} mm</strong></span><span>${this.html(`pedal_${this.answers.pedal}`)}</span><button type="button" class="fit-text-button" data-action="edit">${this.html('edit_answers')}</button></div><aside class="fit-width-pending"><strong>${this.html(this.result.fitTier==='wide'?'wide_last_title':'width_matched_title')}</strong><p>${this.html('width_matched_text')}</p>${this.result.fitTier==='wide'?`<p>${this.html('wide_last_text')}</p>`:''}<a href="${escapeHTML(this.contactHref())}">${this.html('width_contact')}</a></aside>`;
  }

  contactHref() {
    const href=String(this.config.contact || 'mailto:service@santic.com');
    if(href.startsWith('mailto:')) {
      const body=`${this.t('support_answers')}\n${this.t('measurement_summary')}: ${this.answers.lengthMm} mm\n${this.t('width_label')}: ${this.answers.widthMm} mm\n${this.t('instep_title')}: ${this.t(this.answers.instep)}\n${this.t('arch_title')}: ${this.t(this.answers.arch==='unknown'?'unknown':`${this.answers.arch}_arch`)}\n${this.t('support_toe')}: ${this.t({none:'toe_none',slight:'toe_slight',noticeable:'toe_noticeable',unknown:'unknown'}[this.answers.toeShape])} / ${this.t({none:'comfort_none',occasional:'comfort_occasional',frequent:'comfort_frequent',unknown:'unknown'}[this.answers.toeComfort] || 'unknown')}`;
      const pedalBody=`${this.t('pedal_title')}: ${this.t(this.answers.pedal?`pedal_${this.answers.pedal}`:'unknown')}`;
      return `${href.split('?')[0]}?subject=${encodeURIComponent(this.t('support_subject'))}&body=${encodeURIComponent(`${pedalBody}\n${body}`)}`;
    }
    return /^https?:\/\//i.test(href) || href.startsWith('/') ? href : 'mailto:service@santic.com';
  }

  renderResult() {
    const result=this.result;
    if(result.kind==='support') {
      const reason={pedal_unknown:'support_pedal',length_outside:'support_length',width_outside:'support_width',width_unverified:'support_width_data',no_category:'support_category',no_fit:'support_fit',frequent_discomfort:'support_discomfort',invalid_measurement:'length_error',invalid_width:'width_error'}[result.reason] || 'support_unknown';
      this.stage.innerHTML=`<section class="fit-support fit-stage-enter"><h1 class="fit-title" tabindex="-1" data-focus>${this.html('support_title')}</h1><p class="fit-intro">${this.html('support_intro')}</p><p class="fit-support-reason">${this.html(reason)}</p><div class="fit-answer-summary"><p>${this.html('pedal_title')} ${this.html(`pedal_${this.answers.pedal}`)}</p><p>${this.html('measurement_summary')}: <strong>${escapeHTML(this.answers.lengthMm)} mm</strong></p><p>${this.html('width_label')}: <strong>${escapeHTML(this.answers.widthMm)} mm</strong></p></div>${result.reason==='frequent_discomfort'?`<p class="fit-support-health">${this.html('support_health')}</p>`:''}<div class="fit-actions"><a class="fit-button" href="${escapeHTML(this.contactHref())}">${this.html('contact')}${arrow()}</a><button class="fit-text-button" type="button" data-action="edit">${this.html('edit_answers')}</button></div><p class="fit-field-note">${this.html('support_privacy')}</p></section>`;
      return;
    }
    this.stage.innerHTML=`<section class="fit-results fit-stage-enter"><header class="fit-results-head"><h1 class="fit-title" tabindex="-1" data-focus>${this.html('results_title')}</h1><p class="fit-intro">${this.html('results_intro')}</p>${this.summary()}</header><div class="fit-grid ${result.insole?'fit-grid--with-insole':''}" data-results-grid></div><div class="fit-result-footer"><button type="button" class="fit-button fit-button--secondary" data-action="more">${this.html('show_more')}</button></div><p class="fit-result-disclaimer">${this.html('results_note')}</p></section>`;
    this.renderCards();
  }

  safeLink(value) {
    const url=new URL(value,this.config.storefront || location.origin);
    return ['https:','http:'].includes(url.protocol)?url.href:'#';
  }

  productLink(product,variantId=null) {
    const origin=location.origin;
    let root=globalThis.Shopify?.routes?.root || this.config.root || '/';
    if(!root.endsWith('/')) root+='/';
    const url=new URL(`${root}products/${encodeURIComponent(product.handle)}`,origin);
    if(variantId) url.searchParams.set('variant',String(variantId));
    return url.href;
  }

  imageFor(id) {
    const filename=productImages[id];
    return filename?new URL(filename,new URL(this.config.guide,location.href)).href:null;
  }

  insoleCard() {
    const arch=this.result.insole;
    const article=document.createElement('article');
    article.className='fit-product fit-insole-product';
    article.dataset.insole=arch;
    const href=this.config.insoleUrl?this.safeLink(this.config.insoleUrl):this.productLink(insoleProduct);
    article.innerHTML=`<div class="fit-product-media"><img src="${escapeHTML(insoleProduct.images[arch])}" alt="${this.html('insole_product_title')} · ${this.html(`insole_${arch}_choice`)}" width="800" height="800" loading="eager" decoding="async"></div><div class="fit-product-body"><h2 class="fit-product-title">${this.html('insole_product_title')}</h2><p class="fit-product-price">${this.html('insole_optional')}</p><p class="fit-product-size">${this.html('insole_choice_label')}<strong>${this.html(`insole_${arch}_choice`)}</strong></p><p class="fit-insole-instruction">${this.html(`insole_${arch}_instruction`)}</p><a class="fit-button" data-insole-link href="${escapeHTML(href)}">${this.html('insole_product_link')}${arrow()}</a><p class="fit-product-note">${this.html('insole_size_note')}</p></div>`;
    return article;
  }

  renderCards() {
    const grid=this.querySelector('[data-results-grid]');
    const present=grid.querySelectorAll('[data-product]').length;
    const entries=this.result.items.slice(present,this.visibleCount);
    const token=this.renderToken;
    entries.forEach((item,i)=>{
      const article=document.createElement('article');
      article.className='fit-product'; article.dataset.product=item.product.id;
      const reasons=new Set(['reason_pedal','reason_size','reason_width']);
      if(this.answers.instep!=='unknown' && item.product.instep?.includes(this.answers.instep)) reasons.add('reason_instep');
      const image=this.imageFor(item.product.id);
      article.innerHTML=`<div class="fit-product-media" data-media>${image?`<img src="${escapeHTML(image)}" alt="Santic ${escapeHTML(item.product.name)}" width="640" height="520" loading="${present+i<3?'eager':'lazy'}" decoding="async">`:`<span class="fit-product-placeholder">Santic ${escapeHTML(item.product.name)}</span>`}</div><div class="fit-product-body"><h2 class="fit-product-title">${escapeHTML(item.product.name)}</h2><p class="fit-product-price" data-price aria-live="polite">${this.html('loading_product')}</p><p class="fit-product-size">${this.html('suggested_size')}<strong>${this.html('eu')} ${escapeHTML(item.size.eu)}</strong></p>${item.sizedUp?`<p class="fit-product-note" data-size-up>${this.html('base_size')}: ${this.html('eu')} ${escapeHTML(item.baseSizeEu)}. ${this.html('size_up_note')}</p>`:''}<p class="fit-product-note">${this.html('chart_length')}: ${escapeHTML(item.size.lengthMm)} mm<br>${this.html('chart_width')}: ${Number.isFinite(item.size.widthMm)?`${escapeHTML(item.size.widthMm)} mm`:this.html('chart_width_unavailable')}</p><ul class="fit-reasons">${[...reasons].map(k=>`<li>${tick}<span>${this.html(k)}</span></li>`).join('')}</ul><a class="fit-button" data-product-link href="${escapeHTML(this.productLink(item.product))}">${this.html('view_shoe')}${arrow()}</a><p class="fit-product-note" data-stock>${this.html('choose_color')}</p></div>`;
      grid.append(article);
      if(present+i===0 && this.result.insole) grid.append(this.insoleCard());
      this.hydrateProduct(article,item,token,present+i);
    });
    this.querySelector('[data-action="more"]').hidden=this.visibleCount>=this.result.items.length;
  }

  async fetchProduct(handle) {
    let root=globalThis.Shopify?.routes?.root || this.config.root || '/';
    if(!root.endsWith('/')) root+='/';
    const endpoint=`${root}products/${encodeURIComponent(handle)}.js`;
    if(productCache.has(endpoint)) return productCache.get(endpoint);
    const promise=(async()=>{
      const controller=new AbortController();
      const timeout=setTimeout(()=>controller.abort(),12000);
      try {
        const response=await fetch(endpoint,{credentials:'same-origin',signal:controller.signal});
        if(!response.ok) throw new Error(`Product request ${response.status}`);
        const product=await response.json();
        if(!product || !Array.isArray(product.variants)) throw new Error('Invalid product response');
        return product;
      } finally { clearTimeout(timeout); }
    })();
    productCache.set(endpoint,promise);
    try { return await promise; } catch(error) { productCache.delete(endpoint); throw error; }
  }

  async hydrateProduct(article,item,token,index) {
    try {
      const product=await this.fetchProduct(item.product.handle);
      if(!this.isConnected || token!==this.renderToken) return;
      const sizeIndex=(product.options || []).findIndex(option=>/size|尺码|taille|größe/i.test(typeof option==='string'?option:option.name));
      const matches=sizeIndex<0?[]:product.variants.filter(v=>variantMatchesSize((v.options || [v.option1,v.option2,v.option3])[sizeIndex],item.size.eu));
      const variant=matches.find(v=>v.available) || matches[0];
      const price=article.querySelector('[data-price]');
      if(variant && Number.isFinite(Number(variant.price))) {
        try { price.textContent=new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:this.config.currency || 'USD'}).format(Number(variant.price)/100); }
        catch { price.textContent=`${(Number(variant.price)/100).toFixed(2)} ${this.config.currency || 'USD'}`; }
      } else { price.textContent=this.t('size_not_listed'); }
      article.querySelector('[data-product-link]').href=this.productLink(item.product,variant?.id);
      article.querySelector('[data-stock]').textContent=!matches.length?this.t('size_not_listed'):!matches.some(v=>v.available)?this.t('size_unavailable'):this.t('choose_color');
      const image=product.featured_image || product.images?.[0];
      if(image) {
        const url=new URL(typeof image==='string'?image:image.src,this.config.storefront || location.origin);
        if(url.protocol==='https:') {
          url.searchParams.set('width','640');
          const img=document.createElement('img'); img.src=url.href; img.alt=product.title || item.product.name;
          img.width=640; img.height=520; img.loading=index<3?'eager':'lazy'; img.decoding='async';
          const media=article.querySelector('[data-media]');
          img.decode().then(()=>{
            if(this.isConnected && token===this.renderToken) media.replaceChildren(img);
          }).catch(()=>{});
        }
      }
    } catch {
      if(token!==this.renderToken || !this.isConnected) return;
      article.querySelector('[data-price]').textContent='';
      article.querySelector('[data-stock]').textContent=this.t('product_unavailable');
      article.querySelector('[data-product-link]').href=this.productLink(item.product);
    }
  }
}

if(globalThis.customElements && !customElements.get('santic-fit-guide')) customElements.define('santic-fit-guide',SanticFitGuide);
export { SanticFitGuide };
